import { AssetComponentModel, AssetModel, SystemTypePpu } from '@customer/store/apis/assetsApi';
import styles from './AssetPanelDetails.module.scss';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import CasAccordion from '@customer/components/accordions/accordion/CasAccordion';
import AccordionHeader from '@customer/components/accordions/header/AccordionHeader';
import { AccordionStatus } from '@customer/components/accordions/types';
import { AssetDetailsAccordion } from '@customer/store/slices/globalSlice';
import { useAppSelector } from '@customer/store/hooks';
import MapView from '@customer/components/maps/map-view/MapView';
import SingleAssetPositionLayer from '@customer/components/maps/single-asset-pos/SingleAssetPositionLayer';
import {
  ReportingItem,
  useAssetReportingTableData,
} from '@customer/hooks/assets/useAssetReportingTableData';
import AssetInfo, { AssetInfoRow } from '@customer/components/asset-data/asset-info/AssetInfo';
import { AccordionTable } from '@customer/components/accordions/table/AccordionTable';
import {
  columnDefComponentType,
  columnDefFirmwareVersion,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef, RowClassParams, RowClassRules } from 'ag-grid-community';
import { useAssetPositionsOnMap } from '@customer/components/maps/hooks/useAssetPositionsOnMap';
import { getObjectType } from '@cas/utils/translationHelpers';
import { useReportingColDefs } from '@customer/routes/asset/details/useReportingColDefs';

const gridSliceKeyReporting = 'AssetPanelReporting';
const gridSliceKeyComponents = 'AssetPanelComponents';

const getRowIdReporting = (data: ReportingItem) => data.evt;
const getRowIdComponents = (data: AssetComponentModel) => data.type;

const reportRowClassRules: RowClassRules<ReportingItem> = {
  'dm-bold-row': (params: RowClassParams<ReportingItem>) => Boolean(params.data?.error),
};

export interface AssetPanelDetailsProps {
  asset: AssetModel;
}

export function AssetPanelDetails({ asset }: AssetPanelDetailsProps) {
  const { t } = useTranslation();

  const globalData = useAppSelector(state => state.global);

  // Reporting
  const reportingStatus = useAssetReportingTableData({ asset });

  const columnDefsReporting = useReportingColDefs();

  // Components
  const columnDefsComponents: ColDef<AssetComponentModel>[] = useMemo(
    () => [{ ...columnDefComponentType(t, asset.hw) }, { ...columnDefFirmwareVersion(t) }],
    [asset.hw, t],
  );

  const infoRows: AssetInfoRow[] = useMemo(
    () => [
      {
        label: t('Device ID'),
        value: asset.did,
        emphasis: true,
      },
      {
        label: t('ObjectType'),
        value: getObjectType(t, asset.obj),
        emphasis: true,
      },
      {
        label: t('Description'),
        value: asset.desc,
        emphasis: true,
      },
      {
        label: t('CasSoftware'),
        value: asset.casSw,
        displayCondition: asset.hw !== SystemTypePpu,
      },
      {
        label: t('CasOS'),
        value: asset.osVer,
        displayCondition: asset.hw !== SystemTypePpu,
      },
    ],
    [t, asset.did, asset.obj, asset.desc, asset.casSw, asset.hw, asset.osVer],
  );

  const mapProps = useAssetPositionsOnMap(asset);

  return (
    <div className={styles.container}>
      <AssetInfo rows={infoRows} />

      <div className={styles.accordions}>
        <CasAccordion
          key={AssetDetailsAccordion.ReportingStatus}
          defaultExpanded={
            globalData.sidePanelDefaultAccordion === AssetDetailsAccordion.ReportingStatus
          }
          status={reportingStatus.good ? AccordionStatus.Healthy : AccordionStatus.Danger}
          icon={reportingStatus.good ? 'good' : 'information'}
          header={
            <AccordionHeader title={t('ReportingStatus')} subtitle={reportingStatus.subtext} />
          }
        >
          <AccordionTable hasRows={!!reportingStatus.items?.length}>
            <GridControlled<ReportingItem>
              autoheight={true}
              sliceKey={gridSliceKeyReporting}
              rowData={reportingStatus.items}
              columnDefs={columnDefsReporting}
              getRowId={getRowIdReporting}
              rowClassRules={reportRowClassRules}
            />
          </AccordionTable>
        </CasAccordion>

        {asset.hw !== SystemTypePpu && (
          <CasAccordion
            key={AssetDetailsAccordion.Components}
            defaultExpanded={
              globalData.sidePanelDefaultAccordion === AssetDetailsAccordion.Components
            }
            status={AccordionStatus.Informative}
            icon="asset"
            header={<AccordionHeader title={t('Components')} subtitle={t('OpenToViewDetails')} />}
          >
            <AccordionTable
              hasRows={!!reportingStatus.items?.length}
              noRowsMessage={t('No components available')}
            >
              <GridControlled<AssetComponentModel>
                autoheight={true}
                sliceKey={gridSliceKeyComponents}
                rowData={asset.components}
                columnDefs={columnDefsComponents}
                getRowId={getRowIdComponents}
              />
            </AccordionTable>
          </CasAccordion>
        )}

        {(asset.posG || asset.posS) && (
          <CasAccordion
            key={AssetDetailsAccordion.Location}
            defaultExpanded={
              globalData.sidePanelDefaultAccordion === AssetDetailsAccordion.Location
            }
            status={AccordionStatus.Informative}
            icon="map"
            header={
              <AccordionHeader title={t('AssetLocation')} subtitle={t('OpenToViewDetails')} />
            }
          >
            <div className={styles.map}>
              <MapView {...mapProps}>
                <SingleAssetPositionLayer asset={asset} />
              </MapView>
            </div>
          </CasAccordion>
        )}
      </div>
    </div>
  );
}

export default AssetPanelDetails;
